// The flight plan as a list to key into a panel GPS such as the Garmin GNS 430.
//
// The unit's database holds aerodromes and the AIP's IFR reporting points.
// A VRP, a member's own waypoint or a point dropped on the map is not in it:
// the pilot makes it as a user waypoint (WPT, User Waypoint page) from its
// coordinates before building the flight plan, and the unit takes at most
// five characters for the identifier. Pure, so the page computes it offline.

import { distanceNm, normaliseDegrees, trueCourse } from './navigation'

/** The identifier length a GNS 430 user waypoint takes. */
export const GPS_IDENTIFIER_LENGTH = 5

export interface GpsLegInput {
  code: string
  name?: string | null
  lat: number | null
  lon: number | null
  waypointCode?: string | null
}

/** 'database': enter it by its own code. 'user': create it first. */
export type GpsPointKind = 'database' | 'user'

export interface GpsPoint {
  /** Place in the plan, from 0. */
  index: number
  kind: GpsPointKind
  /** The plan's own code for the point. */
  code: string
  name: string | null
  lat: number | null
  lon: number | null
  /** What to key into the flight plan: the code itself, or the suggested user identifier. */
  identifier: string
}

const ICAO = /^[A-Z]{4}$/

/** What a pilot may type as an identifier: capitals and digits, five at most. */
export const sanitiseIdentifier = (text: string) =>
  text.toUpperCase().replaceAll(/[^A-Z0-9]/g, '').slice(0, GPS_IDENTIFIER_LENGTH)

/** One user waypoint: the same code at the same place is the same waypoint. */
export const gpsPointKey = (leg: Pick<GpsLegInput, 'code' | 'lat' | 'lon'>) =>
  `${leg.code.toUpperCase()}|${leg.lat?.toFixed(5)}|${leg.lon?.toFixed(5)}`

/**
 * Whether the unit's database will know the point. `waypointType` is the
 * type of the waypoint row the leg came from, when it still exists ('ad'
 * aerodrome, 'vrp', 'udef' a member's own).
 */
export function gpsPointKind(leg: GpsLegInput, waypointType?: string | null): GpsPointKind {
  // An IFR reporting point from the AIP's ENR 4.4 is in the database.
  if (!leg.waypointCode && leg.name?.startsWith('ENR 4.4')) return 'database'
  if (waypointType === 'ad') return 'database'
  if (waypointType) return 'user'
  return leg.waypointCode && ICAO.test(leg.code.toUpperCase()) ? 'database' : 'user'
}

/**
 * A short identifier for a user waypoint: the code itself when it fits, else
 * its first characters, with a digit tail to keep it apart from `taken`.
 */
export function suggestIdentifier(code: string, taken: ReadonlySet<string>): string {
  const base = code.toUpperCase().replaceAll(/[^A-Z0-9]/g, '') || 'WPT'
  const first = base.slice(0, GPS_IDENTIFIER_LENGTH)
  if (!taken.has(first)) return first
  // Count on from a number the code already ends in (WP1 -> WP2), else from 1.
  const [, stem = base, tail = ''] = /^(.*?)(\d*)$/.exec(first) ?? []
  for (let n = tail ? Number(tail) + 1 : 1; n < 1000; n++) {
    const suffix = String(n)
    const candidate = `${stem.slice(0, GPS_IDENTIFIER_LENGTH - suffix.length)}${suffix}`
    if (!taken.has(candidate)) return candidate
  }
  return first
}

/**
 * Every point of the plan in order, each with what to key in. The same
 * point flown twice (a round trip) gets the same identifier both times.
 */
export function gpsPoints(
  legs: GpsLegInput[],
  waypointTypes: ReadonlyMap<string, string> = new Map(),
  /** The pilot's own identifiers, by gpsPointKey; one that is empty or already taken is ignored. */
  overrides: ReadonlyMap<string, string> = new Map(),
): GpsPoint[] {
  const databaseCodes = legs.flatMap((leg) =>
    gpsPointKind(leg, leg.waypointCode ? waypointTypes.get(leg.waypointCode) : null) === 'database' ? [leg.code.toUpperCase()] : [],
  )
  const taken = new Set(databaseCodes)
  const made = new Map<string, string>()
  // The pilot's choices come first, so a suggestion never takes one of them.
  for (const leg of legs) {
    const key = gpsPointKey(leg)
    const own = sanitiseIdentifier(overrides.get(key) ?? '')
    if (gpsPointKind(leg, leg.waypointCode ? waypointTypes.get(leg.waypointCode) : null) === 'user' && own && !taken.has(own) && !made.has(key)) {
      made.set(key, own)
      taken.add(own)
    }
  }
  return legs.map((leg, index) => {
    const kind = gpsPointKind(leg, leg.waypointCode ? waypointTypes.get(leg.waypointCode) : null)
    if (kind === 'database') return { index, kind, code: leg.code, name: leg.name ?? null, lat: leg.lat, lon: leg.lon, identifier: leg.code.toUpperCase() }
    // Same code at the same place is the same user waypoint.
    const key = gpsPointKey(leg)
    let identifier = made.get(key)
    if (!identifier) {
      identifier = suggestIdentifier(leg.code, taken)
      taken.add(identifier)
      made.set(key, identifier)
    }
    return { index, kind, code: leg.code, name: leg.name ?? null, lat: leg.lat, lon: leg.lon, identifier }
  })
}

/** The user waypoints to create, each once, in the order they are first flown. */
export function userWaypointsToCreate(points: GpsPoint[]): GpsPoint[] {
  const seen = new Set<string>()
  return points.filter((p) => p.kind === 'user' && !seen.has(p.identifier) && seen.add(p.identifier))
}

function degreesMinutes(value: number, hemispheres: [string, string], degreeDigits: number) {
  const abs = Math.abs(value)
  let degrees = Math.floor(abs)
  let minutes = Math.round((abs - degrees) * 60 * 100) / 100
  if (minutes >= 60) {
    degrees += 1
    minutes = 0
  }
  return `${hemispheres[value < 0 ? 1 : 0]}${String(degrees).padStart(degreeDigits, '0')}°${minutes.toFixed(2).padStart(5, '0')}'`
}

/** A latitude as the GNS 430 asks for it: N60°39.26' (degrees, decimal minutes). */
export const formatGpsLat = (lat: number) => degreesMinutes(lat, ['N', 'S'], 2)
/** A longitude as the GNS 430 asks for it: E024°52.87'. */
export const formatGpsLon = (lon: number) => degreesMinutes(lon, ['E', 'W'], 3)

export interface GpsReferenceCandidate {
  code: string
  lat: number
  lon: number
  /** Magnetic variation, east positive. */
  variation: number | null
}

/** A user waypoint as the unit takes it from a known one: radial (magnetic) and distance. */
export interface GpsReference {
  code: string
  radial: number
  distanceNm: number
}

/**
 * The nearest of `candidates` (waypoints the unit knows), and the radial from
 * it to the point: degrees magnetic to the whole degree, distance to 0.1 NM,
 * as the unit's fields take them.
 */
export function gpsReference(point: GpsPoint, candidates: GpsReferenceCandidate[]): GpsReference | null {
  if (point.lat === null || point.lon === null) return null
  let best: { c: GpsReferenceCandidate; d: number } | null = null
  for (const c of candidates) {
    const d = distanceNm(c.lat, c.lon, point.lat, point.lon)
    if (!best || d < best.d) best = { c, d }
  }
  if (!best) return null
  const magnetic = normaliseDegrees(trueCourse(best.c.lat, best.c.lon, point.lat, point.lon) - (best.c.variation ?? 0))
  const radial = Math.round(magnetic) % 360
  return { code: best.c.code, radial: radial === 0 ? 360 : radial, distanceNm: Math.round(best.d * 10) / 10 }
}
